import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import { Newspaper, Pin, Calendar, Megaphone, Rocket, BookOpen, ArrowLeft } from 'lucide-react';
import PortalBottomNav from '../components/PortalBottomNav';
import PublicChatWidget from '../components/PublicChatWidget';

const CAT_META = {
  announcement: { label: 'Announcement', icon: Megaphone, bg: 'bg-violet-50', text: 'text-violet-700' },
  release: { label: 'Release', icon: Rocket, bg: 'bg-emerald-50', text: 'text-emerald-700' },
  blog: { label: 'Blog', icon: BookOpen, bg: 'bg-amber-50', text: 'text-amber-700' },
};

const PublicNews = () => {
  const { subdomain } = useParams();
  const [searchParams] = useSearchParams();
  const accessKey = searchParams.get('key') || '';
  const [workspace, setWorkspace] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => { load(); }, [subdomain]);

  const load = async () => {
    setLoading(true);
    try {
      const keyParam = accessKey ? `?key=${accessKey}` : '';
      const [ws, news] = await Promise.all([
        axios.get(`${API_URL}/public/workspaces/${subdomain}${keyParam}`),
        axios.get(`${API_URL}/public/workspaces/${subdomain}/news`),
      ]);
      setWorkspace(ws.data);
      setItems(news.data.items || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = workspace?.settings?.primary_color || '#7c3aed';

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="public-news-page">
      {/* Hero */}
      <div className="relative" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 py-12 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Newspaper className="h-6 w-6 opacity-80" />
            <span className="text-sm font-medium opacity-90">{workspace?.name}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            News & Updates
          </h1>
          <p className="mt-2 text-white/80">Product announcements, release notes, and what we're shipping.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {selected ? (
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6" data-testid="public-news-detail">
            <button onClick={() => setSelected(null)} className="text-sm text-zinc-500 hover:text-zinc-900 mb-4 flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />Back to all
            </button>
            <NewsBadge category={selected.category} />
            <h2 className="text-3xl font-bold text-zinc-900 mt-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{selected.title}</h2>
            <div className="flex items-center gap-3 text-xs text-zinc-500 mt-2">
              <span>by {selected.author_name}</span>
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(selected.created_at).toLocaleDateString()}</span>
            </div>
            {selected.cover_image_url && (
              <img src={selected.cover_image_url} alt="" className="w-full rounded-xl my-4" />
            )}
            <article className="prose prose-zinc max-w-none mt-4" dangerouslySetInnerHTML={{ __html: selected.content }} />
          </div>
        ) : loading ? (
          <p className="text-center py-12 text-zinc-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200">
            <Newspaper className="h-12 w-12 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500">No news posts yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className="w-full text-left bg-white border border-zinc-200 rounded-2xl p-5 hover:shadow-md hover:border-zinc-300 transition-all"
                data-testid={`news-card-${item.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <NewsBadge category={item.category} />
                  {item.is_pinned && <Pin className="h-3 w-3 text-amber-500" />}
                </div>
                <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</h3>
                {item.excerpt && <p className="text-sm text-zinc-600 mt-1">{item.excerpt}</p>}
                <div className="text-xs text-zinc-400 mt-3 flex items-center gap-2">
                  <span>{item.author_name}</span>
                  <span>·</span>
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <PortalBottomNav workspace={workspace} accessKey={accessKey} />
      {workspace?.settings?.show_ai_chat !== false && (
        <>
          {!chatOpen && (
            <button
              onClick={() => setChatOpen(true)}
              className="fixed bottom-24 right-6 z-50 h-14 px-5 rounded-full shadow-xl text-white font-semibold hover:scale-105 transition-transform"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
              data-testid="news-chat-launcher"
            >
              {workspace?.settings?.ai_button_text || 'Ask AI'}
            </button>
          )}
          {chatOpen && (
            <PublicChatWidget
              subdomain={subdomain}
              workspaceName={workspace?.name}
              primaryColor={primaryColor}
              onClose={() => setChatOpen(false)}
              settings={workspace?.settings}
              accessKey={accessKey}
            />
          )}
        </>
      )}
    </div>
  );
};

const NewsBadge = ({ category }) => {
  const meta = CAT_META[category] || CAT_META.announcement;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${meta.bg} ${meta.text}`}>
      <Icon className="h-3 w-3" />{meta.label}
    </span>
  );
};

export default PublicNews;
